import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Link, Container, Paper, Alert } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Support } from '@mui/icons-material';
import MetaTags from '../../components/SEO/MetaTags';
// Use logo from public folder to avoid build path issues
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { getFirebaseErrorMessage, isTemporaryError } from '../../utils/firebaseErrors';

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

const ForgotPassword = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { resetPassword, user } = useAuth();
  const { logPageView, logFormSubmission, logEvent } = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    logPageView('forgot_password_page');
  }, [logPageView]);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        setMessage('');
        setError('');
        setLoading(true);
        const { error } = await resetPassword(values.email);
        
        if (error) {
          const errorMessage = getFirebaseErrorMessage(error);
          setError(errorMessage);
          logFormSubmission('password_reset_form', {
            success: false,
            error: error.code || 'unknown',
            email_domain: values.email.split('@')[1]
          });
        } else {
          setMessage('Check your inbox for password reset instructions');
          formik.resetForm();
          logFormSubmission('password_reset_form', {
            success: true,
            email_domain: values.email.split('@')[1]
          });
          logEvent('password_reset_requested', {
            email_domain: values.email.split('@')[1]
          });
        }
      } catch (err) {
        setError('Failed to reset password');
        logFormSubmission('password_reset_form', {
          success: false,
          error: 'unknown_error'
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <MetaTags
        title="Forgot Password"
        description="Reset your SurveySlack password - Get back to creating amazing surveys"
        canonical="https://surveyslack.com/forgot-password"
      />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <img src={logo} alt="SurveySlack" style={{ height: '50px' }} />
            </Box>
            <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
              Reset Password
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you instructions to reset your password
            </Typography>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 2,
                  position: 'relative',
                  '& .MuiAlert-message': {
                    width: '100%',
                    paddingBottom: '32px' // Space for the button
                  }
                }}
              >
                {error}
                <Button 
                  color="inherit"
                  size="small"
                  startIcon={<Support />}
                  href="mailto:support@surveyslack.com"
                  sx={{ 
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Contact Support
                </Button>
              </Alert>
            )}
            {message && (
              <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                {message}
              </Alert>
            )}
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                Send Reset Instructions
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Link 
                  component={RouterLink} 
                  to="/login" 
                  variant="body2"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  Back to Sign In
                </Link>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Need help?
                </Typography>
                <Link
                  href="mailto:support@surveyslack.com"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Contact Support
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default ForgotPassword;
